// ============================================
// BlockedMessages.js — Admin Inbox for Blocked Users
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import socketService from '../../services/socket';

const STATUS_COLORS = {
    unread: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    read: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    replied: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};
const ROLE_COLORS = {
    driver: 'bg-indigo-500/10 text-indigo-400',
    pa: 'bg-purple-500/10 text-purple-400',
};

const BlockedMessages = () => {
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [reply, setReply] = useState('');
    const [replying, setReplying] = useState(false);
    const [replySuccess, setReplySuccess] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [search, setSearch] = useState('');

    const fetchMessages = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/blocked-messages${filterStatus ? `?status=${filterStatus}` : ''}`);
            setMessages(data.data.messages);
            setUnreadCount(data.data.unreadCount);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => {
        fetchMessages();

        // ── Real-time Socket Listener ──
        const handleNewMessage = (newMsg) => {
            setMessages(prev => {
                // Prevent duplicates
                if (prev.some(m => m._id === newMsg._id)) return prev;
                return [newMsg, ...prev];
            });
            setUnreadCount(prev => prev + 1);
        };

        socketService.on('blocked_message:new', handleNewMessage);

        return () => {
            socketService.off('blocked_message:new', handleNewMessage);
        };
    }, [fetchMessages]);

    const openMessage = async (msg) => {
        setSelected(msg);
        setReply('');
        setReplySuccess(false);
        if (msg.status === 'unread') {
            try {
                await api.patch(`/admin/blocked-messages/${msg._id}/read`);
                setMessages(prev => prev.map(m => m._id === msg._id ? { ...m, status: 'read' } : m));
                setUnreadCount(prev => Math.max(0, prev - 1));
                setSelected(prev => prev ? { ...prev, status: 'read' } : prev);
            } catch (e) {}
        }
    };

    const sendReply = async () => {
        if (!reply.trim() || !selected) return;
        setReplying(true);
        try {
            await api.post(`/admin/blocked-messages/${selected._id}/reply`, { reply });
            setMessages(prev => prev.map(m => m._id === selected._id ? { ...m, status: 'replied', adminReply: reply } : m));
            setSelected(prev => ({ ...prev, status: 'replied', adminReply: reply }));
            setReplySuccess(true);
            setReply('');
        } catch (e) {
            alert('Failed to send reply. Please try again.');
        } finally {
            setReplying(false);
        }
    };

    const filtered = messages.filter(m =>
        !search || m.senderEmail.includes(search) || m.senderName?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <AdminLayout title="Blocked User Messages" subtitle="Review and respond to blocked account appeals">
            <div className="flex gap-6 h-[calc(100vh-8rem)] min-h-0">

                {/* ── Left: Message List ── */}
                <div className={`${selected ? 'hidden lg:flex' : 'flex'} w-full lg:w-[380px] xl:w-[420px] flex-shrink-0 flex-col gap-3 min-h-0`}>
                    {/* Filters */}
                    <div className="flex gap-2 flex-wrap">
                        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-2 rounded-xl bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 backdrop-blur-sm">
                            <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sender..." className="bg-transparent text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 outline-none flex-1" />
                        </div>
                        <div className="relative flex-shrink-0">
                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="appearance-none pr-9 pl-4 py-2 rounded-xl bg-white/50 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-700/50 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none backdrop-blur-sm cursor-pointer focus:ring-2 focus:ring-indigo-500/30 transition-all hover:bg-white dark:hover:bg-slate-800 w-[120px]">
                                <option value="" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">All</option>
                                <option value="unread" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">Unread</option>
                                <option value="read" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">Read</option>
                                <option value="replied" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold">Replied</option>
                            </select>
                            <svg className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                    </div>

                    {/* Stats bar */}
                    {unreadCount > 0 && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-500/20">
                            <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                            <span className="text-xs font-bold text-rose-400">{unreadCount} unread {unreadCount === 1 ? 'message' : 'messages'}</span>
                        </div>
                    )}

                    {/* List */}
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0 sidebar-scroll">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-40 gap-3">
                                <div className="w-8 h-8 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                                <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Loading...</p>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-40 gap-3 text-center">
                                <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                                </div>
                                <p className="text-sm font-bold text-slate-500">No messages yet</p>
                            </div>
                        ) : filtered.map(msg => (
                            <button key={msg._id} onClick={() => openMessage(msg)} className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 backdrop-blur-sm group ${selected?._id === msg._id ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-white/50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700/50 hover:border-indigo-500/30 hover:bg-indigo-500/5'}`}>
                                <div className="flex items-start justify-between gap-2 mb-1.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className={`w-8 h-8 rounded-xl flex-shrink-0 flex items-center justify-center text-xs font-black ${ROLE_COLORS[msg.senderRole] || 'bg-slate-500/10 text-slate-400'}`}>
                                            {msg.senderName?.[0]?.toUpperCase() || '?'}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{msg.senderName || 'Unknown'}</p>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{msg.senderEmail}</p>
                                        </div>
                                    </div>
                                    <span className={`flex-shrink-0 px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wider ${STATUS_COLORS[msg.status]}`}>{msg.status}</span>
                                </div>
                                <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-300 truncate mb-1">{msg.subject}</p>
                                <p className="text-[12px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{msg.message}</p>
                                <p className="text-[10px] text-slate-400 mt-2">{new Date(msg.createdAt).toLocaleString('en-GB')}</p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Right: Message Detail ── */}
                <div className={`${selected ? 'flex' : 'hidden lg:flex'} flex-1 min-w-0 flex-col bg-white/50 dark:bg-slate-900/40 backdrop-blur-sm rounded-3xl border border-slate-200 dark:border-slate-700/50 overflow-hidden`}>
                    {!selected ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
                            <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 flex items-center justify-center">
                                <svg className="w-8 h-8 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                            </div>
                            <div>
                                <p className="text-base font-bold text-slate-700 dark:text-white mb-1">Select a message</p>
                                <p className="text-sm text-slate-500">Choose a message from the list to view and respond</p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full min-h-0">
                            {/* Header */}
                            <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700/50 flex-shrink-0">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <button onClick={() => setSelected(null)} className="lg:hidden p-2 -ml-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors mt-0.5">
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-base font-black flex-shrink-0 ${ROLE_COLORS[selected.senderRole] || 'bg-slate-500/10 text-slate-400'}`}>
                                            {selected.senderName?.[0]?.toUpperCase() || '?'}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-bold text-slate-900 dark:text-white truncate">{selected.senderName}</p>
                                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">{selected.senderEmail}</p>
                                            <div className="flex sm:hidden flex-wrap items-center gap-1.5 mt-2">
                                                <span className={`px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-wider ${ROLE_COLORS[selected.senderRole] || ''}`}>{selected.senderRole?.toUpperCase()}</span>
                                                <span className={`px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-wider ${STATUS_COLORS[selected.status]}`}>{selected.status}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                                        <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${ROLE_COLORS[selected.senderRole] || ''}`}>{selected.senderRole?.toUpperCase()}</span>
                                        <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${STATUS_COLORS[selected.status]}`}>{selected.status}</span>
                                    </div>
                                </div>
                                <h3 className="mt-3 font-bold text-slate-800 dark:text-white">{selected.subject}</h3>
                                <p className="text-xs text-slate-400 mt-0.5">{new Date(selected.createdAt).toLocaleString('en-GB')}</p>
                            </div>

                            {/* Message Body */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0 sidebar-scroll">
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/50">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">User Message</p>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                                </div>

                                {selected.adminReply && (
                                    <div className="bg-indigo-500/5 rounded-2xl p-5 border border-indigo-500/20">
                                        <p className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-3">Your Reply</p>
                                        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{selected.adminReply}</p>
                                        <p className="text-xs text-slate-400 mt-3">{selected.repliedAt ? new Date(selected.repliedAt).toLocaleString('en-GB') : ''}</p>
                                    </div>
                                )}
                            </div>

                            {/* Reply Box */}
                            <div className="px-6 py-5 border-t border-slate-200 dark:border-slate-700/50 flex-shrink-0">
                                {replySuccess ? (
                                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                                        <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        <p className="text-sm font-bold text-emerald-400">Reply sent successfully. Email delivered to {selected.senderEmail}.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <label className="block text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                                            {selected.status === 'replied' ? 'Send Another Reply' : 'Reply to User'}
                                        </label>
                                        <textarea
                                            value={reply}
                                            onChange={e => setReply(e.target.value)}
                                            rows={4}
                                            placeholder="Write your response here. It will be emailed to the user immediately..."
                                            className="w-full px-4 py-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm resize-none leading-relaxed"
                                        />
                                        <div className="flex justify-end">
                                            <button onClick={sendReply} disabled={replying || !reply.trim()} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 shadow-lg shadow-indigo-500/20">
                                                {replying ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</> : <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg> Send Reply via Email</>}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default BlockedMessages;
